import { KVStore } from '../KVStore';
import { ClientOperationReportRecord, ClientOperationReportRecordPatch, ClientReportOpType } from './clientReportTypes';

export class ClientReportStore {
  private kv: KVStore;
  private tailByKey: Map<string, Promise<void>>;

  constructor(prefix: string) {
    this.kv = new KVStore(prefix);
    this.tailByKey = new Map();
  }

  /**
   * 串行化同一个 reportKey 的写入，避免并发 upsert 造成字段被空值覆盖
   * （例如：onScanTriggered / onRequest 同时触发时，最后一次写入可能覆盖 barCode/touchAtMs）
   */
  private async runSerialized<T>(key: string, fn: () => Promise<T>): Promise<T> {
    const prev = this.tailByKey.get(key) ?? Promise.resolve();
    let release!: () => void;
    const next = new Promise<void>(resolve => {
      release = resolve;
    });

    this.tailByKey.set(
      key,
      prev
        .then(() => next)
        .catch(() => next)
    );

    await prev;
    try {
      return await fn();
    } finally {
      release();
      if (this.tailByKey.get(key) === next) {
        this.tailByKey.delete(key);
      }
    }
  }

  async upsert(
    reportKey: string,
    opType: ClientReportOpType,
    patch: ClientOperationReportRecordPatch
  ): Promise<boolean> {
    return this.runSerialized(reportKey, async () => {
      const now = Date.now();
      const existing = await this.kv.getItem<ClientOperationReportRecord>(reportKey);
      const base: ClientOperationReportRecord = existing || {
        reportKey,
        opType,
        barCode: patch.barCode || '',
        createdAtMs: now,
        updatedAtMs: now
      };

      const merged: ClientOperationReportRecord = {
        ...base,
        ...patch,
        opType,
        reportKey,
        barCode: patch.barCode ?? base.barCode,
        deviceId: patch.deviceId ?? base.deviceId,
        clientIp: patch.clientIp ?? base.clientIp,
        updatedAtMs: now,
        attemptCount: patch.attemptCount ?? base.attemptCount
      };

      return this.kv.setItem(reportKey, merged);
    });
  }

  async getItem(reportKey: string): Promise<ClientOperationReportRecord | null> {
    return this.kv.getItem<ClientOperationReportRecord>(reportKey);
  }

  async remove(reportKey: string): Promise<boolean> {
    return this.kv.removeItem(reportKey);
  }

  async removeMany(reportKeys: string[]): Promise<void> {
    await Promise.all(reportKeys.map(k => this.kv.removeItem(k)));
  }

  async listReady(
    limit: number,
    minAgeMs: number
  ): Promise<ClientOperationReportRecord[]> {
    const now = Date.now();
    const items = await this.kv.getAllItems<ClientOperationReportRecord>();

    const eligible = items.filter(item => {
      const t = item.touchAtMs ?? item.createdAtMs;
      return t <= now - minAgeMs;
    });

    eligible.sort((a, b) => {
      const ta = a.touchAtMs ?? a.createdAtMs;
      const tb = b.touchAtMs ?? b.createdAtMs;
      return ta - tb;
    });

    return eligible.slice(0, Math.max(0, limit));
  }
}


